import { useState, useEffect, lazy, Suspense } from "react";
import { Leaf, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { usePostAuthFlow } from "@/hooks/usePostAuthFlow";
import UserProfile from "@/components/UserProfile";
import { Button } from "@/components/ui/button";
import { C } from "@/components/landing/brandTokens";
import OpeningSection from "@/components/landing/OpeningSection";
import HeroSection from "@/components/landing/HeroSection";
import ProductVisualSection from "@/components/landing/ProductVisualSection";
import ProblemSection from "@/components/landing/ProblemSection";
import ExperienceSection from "@/components/landing/ExperienceSection";
import ComparisonSection from "@/components/landing/ComparisonSection";
import AudienceSection from "@/components/landing/AudienceSection";
import CompanionsSection from "@/components/landing/CompanionsSection";
import SocialProofSection from "@/components/landing/SocialProofSection";
import FAQSection from "@/components/landing/FAQSection";
import PricingSection from "@/components/landing/PricingSection";
import FooterCTASection from "@/components/landing/FooterCTASection";
import LandingFooter from "@/components/landing/LandingFooter";
import SectionDivider from "@/components/landing/SectionDivider";

const AuthModal = lazy(() => import("@/components/AuthModal"));

const Index = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalDefaultTab, setAuthModalDefaultTab] = useState<"signin" | "signup">("signup");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // navDark: true when scrolled into HeroSection (dark bg) or past it
  const [navDark, setNavDark] = useState(false);
  const navigate = useNavigate();

  usePostAuthFlow({ profile, profileLoading });

  // Scroll-aware nav — tracks both frosted glass threshold and dark-section overlap
  useEffect(() => {
    const heroEl = document.getElementById("hero-section");
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      if (heroEl) {
        const { top, bottom } = heroEl.getBoundingClientRect();
        // Nav (60px tall) overlaps the dark hero when hero top < nav height
        setNavDark(top < 60 && bottom > 0);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-redirect authenticated users
  useEffect(() => {
    if (authLoading || profileLoading) return;
    if (user && profile?.onboarding_completed) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, profile, authLoading, profileLoading, navigate]);

  // Landing CTA source tracking — stored in sessionStorage pre-auth, picked up by
  // AnalyticsIdentityBridge / usePostAuthFlow once PostHog initializes after signup.
  const trackLandingCTA = (source: string, intent: "signup" | "signin") => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem("landing_cta_source", source);
      sessionStorage.setItem("landing_cta_intent", intent);
      sessionStorage.setItem("landing_cta_ts", String(Date.now()));
    } catch {
      // sessionStorage unavailable (private mode, etc.) — no-op
    }
  };

  const handleGetStarted = (source: string = "unknown") => {
    trackLandingCTA(source, "signup");
    if (!user) {
      setAuthModalDefaultTab("signup");
      setShowAuthModal(true);
    } else if (!profile?.onboarding_completed) {
      navigate("/onboarding");
    } else {
      navigate("/dashboard");
    }
  };

  const handleSignIn = (source: string = "unknown") => {
    trackLandingCTA(source, "signin");
    setAuthModalDefaultTab("signin");
    setShowAuthModal(true);
  };

  return (
    <div className="min-h-screen" style={{ background: C.cream }}>
      {/* ── Nav ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between transition-all duration-300"
        style={{
          height: 60,
          padding: "0 clamp(16px, 4vw, 40px)",
          background: scrolled ? (navDark ? "rgba(27,20,16,0.90)" : "rgba(250,248,243,0.96)") : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          borderBottom: scrolled ? `1px solid ${navDark ? "rgba(250,248,243,0.10)" : C.bdr}` : "none",
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div
            className="flex items-center justify-center rounded-full transition-colors duration-300"
            style={{
              width: 28, height: 28,
              background: navDark ? "rgba(107,125,86,0.20)" : C.sageGlow,
              border: `1px solid ${navDark ? "rgba(107,125,86,0.35)" : C.sageBdr}`,
            }}
          >
            <Leaf size={14} className="text-spec-sage" />
          </div>
          <span
            className="font-heading text-xl font-semibold tracking-[0.3px] transition-colors duration-300"
            style={{ color: navDark ? C.cream : C.txt }}
          >
            Eden Pages
          </span>
        </div>

        {/* Center links — desktop */}
        <div className="hidden md:flex items-center gap-1">
          {[
            { label: "See the Product", href: "#the-product" },
            { label: "How It Works", href: "#how-it-works" },
            { label: "Pricing", href: "#pricing" },
            { label: "The Story", href: "#about-eden" },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="font-ui text-xs no-underline rounded-md transition-colors duration-300"
              style={{
                padding: "6px 12px",
                color: navDark ? "rgba(250,248,243,0.60)" : C.txt3,
              }}
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2.5 rounded-lg hover:bg-spec-parch transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {user && profile?.onboarding_completed ? (
            <>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="font-ui text-spec-sage border-spec-sage-bdr hover:bg-spec-sage-glow"
              >
                Dashboard
              </Button>
              <UserProfile size="md" />
            </>
          ) : user ? (
            <>
              <Button
                onClick={() => navigate("/onboarding")}
                className="font-ui bg-spec-sage text-white hover:opacity-90"
              >
                Complete Setup
              </Button>
              <UserProfile size="md" />
            </>
          ) : (
            <>
              <button
                onClick={() => handleSignIn("nav-desktop")}
                className="hidden md:inline-flex font-ui text-[13px] text-spec-txt3 bg-transparent border border-transparent rounded-lg cursor-pointer transition-all hover:border-spec-bdr hover:text-spec-txt2"
                style={{ padding: "10px 18px" }}
                onMouseEnter={() => import("@/components/AuthModal")}
              >
                Sign In
              </button>
              <button
                onClick={() => handleGetStarted("nav-desktop")}
                className="hidden md:inline-flex font-ui text-[13px] font-medium text-white bg-spec-sage border-none rounded-lg cursor-pointer items-center gap-2 transition-all hover:-translate-y-0.5"
                style={{ padding: "10px 22px", boxShadow: "0 2px 8px rgba(107,125,86,0.25)" }}
                onMouseEnter={() => import("@/components/AuthModal")}
              >
                Begin Your Practice
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          className="fixed top-[60px] left-0 right-0 z-[99] md:hidden px-6 py-4 space-y-1"
          style={{ background: C.cream, borderBottom: `1px solid ${C.bdr}` }}
        >
          {[
            { label: "See the Product", href: "#the-product" },
            { label: "How It Works", href: "#how-it-works" },
            { label: "Pricing", href: "#pricing" },
            { label: "The Story", href: "#about-eden" },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setMobileMenuOpen(false)}
              className="block font-ui text-sm text-spec-txt2 py-2 no-underline"
            >
              {l.label}
            </a>
          ))}
          {!user && (
            <div className="flex items-center gap-3 pt-3 border-t border-spec-bdr mt-1">
              <button
                onClick={() => { handleSignIn("nav-mobile"); setMobileMenuOpen(false); }}
                className="font-ui text-sm text-spec-txt2 flex-1 text-center py-2.5 rounded-lg border border-spec-bdr hover:bg-spec-parch transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => { handleGetStarted("nav-mobile"); setMobileMenuOpen(false); }}
                className="font-ui text-sm font-medium text-white bg-spec-sage rounded-lg flex-1 py-2.5 text-center"
                style={{ boxShadow: "0 2px 8px rgba(107,125,86,0.20)" }}
              >
                Begin Your Practice
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Sections ── */}
      <OpeningSection />
      <HeroSection onCTA={() => handleGetStarted("hero")} />
      <ProductVisualSection />
      <ProblemSection onCTA={() => handleGetStarted("problem")} />
      <AudienceSection />
      <ExperienceSection />
      <SectionDivider variant="arc" />
      <ComparisonSection />
      <CompanionsSection />
      <SocialProofSection />
      <SectionDivider variant="hairline" />
      <FAQSection />
      <PricingSection onCTA={() => handleGetStarted("pricing")} />
      <FooterCTASection onCTA={() => handleGetStarted("footer")} />
      <LandingFooter />

      {/* Mobile sticky CTA — appears after hero scroll, hidden once authenticated */}
      {scrolled && !user && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-[95]"
          style={{
            background: "rgba(27,20,16,0.96)",
            backdropFilter: "blur(12px)",
            borderTop: "1px solid rgba(250,248,243,0.10)",
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom)) 16px",
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleGetStarted("mobile-sticky")}
              className="flex-1 font-ui text-[14px] font-medium text-spec-brown bg-spec-cream rounded-lg py-3 active:opacity-90 transition-opacity"
              onTouchStart={() => import("@/components/AuthModal")}
            >
              Start free — 10 mornings
            </button>
            <button
              onClick={() => handleSignIn("mobile-sticky")}
              className="font-ui text-[13px] rounded-lg px-4 py-3"
              style={{
                color: "rgba(250,248,243,0.75)",
                border: "1px solid rgba(250,248,243,0.18)",
              }}
              onTouchStart={() => import("@/components/AuthModal")}
            >
              Sign in
            </button>
          </div>
        </div>
      )}

      {/* Desktop sticky CTA — floating pill bottom-right after hero scroll */}
      {scrolled && !user && (
        <button
          onClick={() => handleGetStarted("desktop-sticky")}
          onMouseEnter={() => import("@/components/AuthModal")}
          className="hidden md:inline-flex fixed z-[95] font-ui text-[13px] font-medium items-center gap-2 rounded-full transition-all duration-200 hover:-translate-y-0.5"
          style={{
            bottom: 28,
            right: 28,
            padding: "12px 22px",
            color: "#1B1410",
            background: C.cream,
            border: `1px solid ${C.bdr}`,
            boxShadow: "0 8px 32px rgba(27,20,16,0.22), 0 2px 8px rgba(27,20,16,0.10)",
            letterSpacing: "0.2px",
          }}
          aria-label="Start free — ten mornings, no credit card"
        >
          Start free — 10 mornings
        </button>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/20 backdrop-blur-sm" />}>
          <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} defaultTab={authModalDefaultTab} />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
